/* eslint-disable max-statements */

import React from 'react';
import PropTypes from 'prop-types';
import {
    useFloating,
    autoUpdate,
    offset,
    size,
    arrow,
    flip,
    shift,
    type Placement,
    FloatingArrow,
} from '@floating-ui/react';
import cx from 'classnames';
import { noop } from 'lodash';

import { TransitionCollapse } from 'components/Transition';
import { Portal } from 'components/Utilities';

import { eventHasKey, KEY } from 'utilsTS/dom';
import {
    useToggleable,
    ToggleableContext,
    type ToggleableProps,
    toggleablePropTypes,
    useId,
    useBrowser,
} from 'utilsTS/hooks';
import { ref, elementOfType } from 'utilsTS/prop-types';
import { cloneElement, isForwardRefComponent, mergeRefs, renderProp } from 'utilsTS/react';

import { useClickOutside } from './useClickOutside';
import { useFocusDetour } from './useFocusDetour';
import styles from './NewDropdown.module.scss';

export enum DROPDOWN_ALIGNMENT {
    LEFT = 'bottom-start',
    CENTER = 'bottom',
    RIGHT = 'bottom-end',
    TOGGLE = 'toggle',
}

export interface NewDropdownProps extends ToggleableProps {
    toggle?: React.ReactNode | ((props: { isOpen?: boolean }) => React.ReactNode);
    renderToggle?: (props: { isOpen?: boolean }) => React.ReactNode;
    actionButton?: React.ReactNode;
    footer?: React.ReactNode;

    placement?: Placement;
    alignment?: DROPDOWN_ALIGNMENT;
    positionHorizontally?: boolean;
    arrow?: boolean;
    padded?: boolean;
    scrollable?: boolean;
    maxHeight?: number | string;
    minWidth?: number;
    offset?: number;
    usePortal?: boolean;
    useFocusTrap?: boolean;
    openOnFocus?: boolean;
    openOnKeyDown?: boolean; // TODO: not used
    toggleOnClick?: boolean;
    toggleWithSpace?: boolean;
    toggleWithEnter?: boolean;
    closeWithTab?: boolean;
    closeOnClickOutside?: boolean;
    closeOnScroll?: boolean;
    disabled?: boolean;
    quick?: boolean;

    toggleRef?: React.Ref<HTMLElement>;
    popoverRef?: React.Ref<HTMLDivElement>;
    dropdownRef?: React.Ref<NewDropdownImperatives>;

    tabIndex?: number;
    className?: string;
    toggleClassName?: string;
    containerClassName?: string;
    dropdownClassName?: string;
    contentClassName?: string;
    role?: string;
    children?: React.ReactNode;

    onKeyDown?: React.KeyboardEventHandler;
    onClick?: React.MouseEventHandler;
    onFocus?: React.FocusEventHandler;
    onClickOutside?: (event: MouseEvent, isClick: boolean) => void;

    defaultOpen?: boolean; // TODO: update ToggleableProps
}

export interface NewDropdownImperatives {
    open: () => void;
    close: () => void;
}

export interface NewDropdownStatics {
    ALIGNMENT: typeof DROPDOWN_ALIGNMENT;
}

export const NewDropdown: React.FC<NewDropdownProps> & NewDropdownStatics = ({
    id: defaultId,
    toggle,
    renderToggle: renderToggleProp,
    actionButton,
    footer,
    placement,
    alignment = DROPDOWN_ALIGNMENT.RIGHT,
    positionHorizontally,
    arrow: arrowProp = true,
    padded = true,
    scrollable = true,
    maxHeight = 0,
    minWidth = 0,
    offset: offsetProp = 8,
    usePortal = true,
    useFocusTrap = true,
    openOnFocus,
    openOnKeyDown,
    toggleOnClick = true,
    toggleWithSpace = true,
    toggleWithEnter = true,
    closeWithTab,
    closeOnClickOutside = true,
    closeOnScroll = true,
    disabled,
    quick,
    toggleRef: toggleRefProp = noop,
    popoverRef: popoverRefProp = noop,
    dropdownRef,
    tabIndex = 0,
    className,
    toggleClassName,
    containerClassName,
    dropdownClassName,
    contentClassName,
    role = 'region',
    children,
    defaultOpen,
    isOpen,
    onBeforeOpen,
    onOpen,
    onBeforeClose,
    onClose,
    onKeyDown = noop,
    onClick = noop,
    onFocus = noop,
    onClickOutside = noop,
    // ...rest // TODO: where should rest go?
}) => {
    const id = useId('dropdown', defaultId);

    const floatingArrowRef = React.useRef<SVGSVGElement>(null!); // eslint-disable-line @typescript-eslint/no-non-null-assertion
    const toggleRef = React.useRef<HTMLButtonElement>(null!); // eslint-disable-line @typescript-eslint/no-non-null-assertion
    const contentRef = React.useRef<HTMLDivElement>(null!); // eslint-disable-line @typescript-eslint/no-non-null-assertion
    const timeoutRef = React.useRef<NodeJS.Timeout>();

    const { window } = useBrowser();

    const getPlacement = () => {
        if (placement) return placement;
        if (positionHorizontally) return 'left';
        if (alignment === DROPDOWN_ALIGNMENT.TOGGLE) return 'bottom';
        return alignment;
    };

    const floatingProps = useFloating({
        placement: getPlacement(),
        whileElementsMounted: autoUpdate,
        middleware: [
            offset(arrowProp ? offsetProp : 2),
            size(
                alignment === DROPDOWN_ALIGNMENT.TOGGLE
                    ? {
                          apply({ rects, elements }) {
                              Object.assign(elements.floating.style, {
                                  width: `${rects.reference.width}px`,
                                  minWidth: `${Math.max(rects.reference.width, minWidth)}px`,
                              });
                          },
                      }
                    : {},
            ),
            flip(),
            shift({ padding: 15 }),
            arrow({ element: floatingArrowRef, padding: 5 }),
        ],
    });

    const [isToggled, { open, close, toggle: doToggle }] = useToggleable(defaultOpen, isOpen, {
        onBeforeOpen,
        onBeforeClose,
    });

    React.useImperativeHandle(dropdownRef, () => ({ open, toggle: doToggle, close }));

    const handleScroll = () => {
        if (closeOnScroll && isToggled) close();
    };

    React.useEffect(() => {
        /* istanbul ignore if */
        if (!window) return noop;

        if (isToggled) {
            timeoutRef.current = setTimeout(() => {
                window.addEventListener('scroll', handleScroll);
            }, 500);
        }

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                window.removeEventListener('scroll', handleScroll);
            }
        };
    }, [isToggled]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleClickOutside = (event: MouseEvent | KeyboardEvent, isClick?: boolean) => {
        const { target } = event;

        if (event instanceof MouseEvent) {
            if (contentRef.current.contains(target as Node)) return;
            if (toggleRef.current === target || toggleRef.current.contains(target as Node)) return;
        }

        if (closeOnClickOutside && isToggled) close();

        onClickOutside(event, isClick);
    };

    const focusDetourRef = useFocusDetour(useFocusTrap && usePortal && isToggled && !disabled);
    const clickOutsideRef = useClickOutside(handleClickOutside, isToggled);

    const handleToggleClick: React.MouseEventHandler = (event) => {
        if (toggleOnClick) doToggle();
        onClick(event);
    };

    const handleToggleFocus: React.FocusEventHandler = (event) => {
        if (openOnFocus) open();
        onFocus(event);
    };

    const handleToggleKeyDown: React.KeyboardEventHandler = (event) => {
        const isEnter = eventHasKey(event, KEY.ENTER) && toggleWithEnter;
        const isSpace = eventHasKey(event, KEY.SPACE) && toggleWithSpace;
        const isTab = eventHasKey(event, KEY.TAB) && closeWithTab;

        if (isEnter || isSpace) {
            event.preventDefault();
            doToggle();
        }

        if (isTab) close();
        if (openOnKeyDown) open();

        onKeyDown(event);
    };

    const renderToggle = () => {
        const renderedToggle = renderProp(toggle, renderToggleProp, { isOpen: isToggled });
        const refProp = isForwardRefComponent(renderedToggle) ? 'ref' : 'innerRef'; // TODO: catch up with no forwardRef branch
        const props = {
            id: `${id}`,
            [refProp]: mergeRefs(floatingProps.refs.setReference, toggleRef, toggleRefProp),
            tabIndex,
            disabled,
            className: cx(styles.toggle, className, toggleClassName),
            'aria-controls': `${id}-popover`,
            'aria-haspopup': true,
            'aria-expanded': isToggled,
            onClick: handleToggleClick,
            onFocus: handleToggleFocus,
            onKeyDown: handleToggleKeyDown,
        };

        return cloneElement(renderedToggle, props);
    };

    const context = React.useMemo(
        () => ({ id, isOpen: isToggled, open, toggle: doToggle, close, onBeforeClose, onClose }),
        [open, doToggle, close, id, isToggled, onBeforeClose, onClose],
    );

    const renderPopover = () => {
        const contentClassNames = cx(styles.content, padded && styles.padded, contentClassName);
        const contentStyle = scrollable ? { maxHeight: maxHeight || undefined } : {};

        const content = scrollable ? (
            <div className={contentClassNames} style={contentStyle}>
                {children}
            </div>
        ) : (
            children
        );

        return (
            <div
                ref={mergeRefs(floatingProps.refs.setFloating, focusDetourRef, popoverRefProp)}
                id={`${id}-popover`}
                role={role}
                className={cx(styles.dropdown, containerClassName)}
                style={floatingProps.floatingStyles}
            >
                <TransitionCollapse
                    nodeRef={contentRef}
                    show={isToggled}
                    horizontal={positionHorizontally}
                    bottom={positionHorizontally || floatingProps.placement.includes('top')}
                    quick={quick}
                    onEntered={onOpen}
                    onExited={onClose}
                >
                    <div
                        ref={mergeRefs(clickOutsideRef, contentRef)}
                        className={cx('g-dropdown', styles.popper, dropdownClassName)}
                    >
                        <ToggleableContext.Provider value={context}>
                            <div className={cx(styles.body)}>
                                {content}
                                {actionButton && (
                                    <div className={styles.action}>{actionButton}</div>
                                )}
                            </div>
                            {footer && <div className={styles.footer}>{footer}</div>}
                            {arrowProp && (
                                <FloatingArrow
                                    ref={floatingArrowRef}
                                    context={floatingProps.context}
                                    staticOffset={
                                        alignment === DROPDOWN_ALIGNMENT.LEFT ? 30 : undefined
                                    }
                                    className="g-dropdown-arrow"
                                />
                            )}
                        </ToggleableContext.Provider>
                    </div>
                </TransitionCollapse>
            </div>
        );
    };

    return (
        <>
            {renderToggle()}
            {!disabled && <Portal active={usePortal}>{renderPopover()}</Portal>}
        </>
    );
};

NewDropdown.ALIGNMENT = DROPDOWN_ALIGNMENT;

NewDropdown.displayName = 'NewDropdown';
NewDropdown.propTypes = {
    ...toggleablePropTypes,
    toggle: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
    renderToggle: PropTypes.func,
    tabIndex: PropTypes.number,
    maxHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    minWidth: PropTypes.number,
    role: PropTypes.string,
    disabled: PropTypes.bool,
    scrollable: PropTypes.bool,
    arrow: PropTypes.bool,
    padded: PropTypes.bool,
    alignment: PropTypes.oneOf(Object.values(DROPDOWN_ALIGNMENT)),
    positionHorizontally: PropTypes.bool,
    actionButton: PropTypes.oneOfType([elementOfType(['DropdownActionButton']), PropTypes.node]),
    offset: PropTypes.number,
    usePortal: PropTypes.bool,
    useFocusTrap: PropTypes.bool,
    openOnFocus: PropTypes.bool,
    openOnKeyDown: PropTypes.bool,
    toggleRef: ref(),
    popoverRef: ref(),
    dropdownRef: ref(),
    toggleOnClick: PropTypes.bool,
    toggleWithSpace: PropTypes.bool,
    toggleWithEnter: PropTypes.bool,
    closeWithTab: PropTypes.bool,
    closeOnClickOutside: PropTypes.bool,
    closeOnScroll: PropTypes.bool,
    quick: PropTypes.bool,
    onClickOutside: PropTypes.func,
    onKeyDown: PropTypes.func,
    onClick: PropTypes.func,
    onFocus: PropTypes.func,
    toggleClassName: PropTypes.string,
    dropdownClassName: PropTypes.string,
    containerClassName: PropTypes.string,
    contentClassName: PropTypes.string,
    className: PropTypes.string,
    footer: PropTypes.oneOfType([elementOfType(['DropdownFooter']), PropTypes.node]),
    children: PropTypes.node,
};
